/** School staff roles (Teacher table); portal route role remains "Teacher". */

import { getHomeroomTeacherTerm, isPrimarySchoolType } from "./homeroomTeacherTerm";
import { getTeacherSubjects } from "./teacherSubjects";

export const STAFF_ROLES = Object.freeze({
    TEACHER: "Teacher",
    FORM_TEACHER: "Form Teacher",
    CLASS_TEACHER: "Class Teacher",
    BURSAR: "Bursar",
    HOD: "Head of Department",
    LIBRARIAN: "Librarian",
});

const BASE_ROLE_OPTIONS = [
    {
        value: STAFF_ROLES.TEACHER,
        label: "Teacher",
        description: "Teaches one or more subjects; submits marks to the homeroom teacher.",
    },
    { value: STAFF_ROLES.BURSAR, label: "Bursar", description: "Handles school fees, expenses, and finance reports." },
    { value: STAFF_ROLES.HOD, label: "Head of Department", description: "Department lead; can be assigned subjects like a teacher." },
    {
        value: STAFF_ROLES.LIBRARIAN,
        label: "Librarian",
        description: "Manages library stock and loans; director can also assign subjects to teach.",
    },
];

const VALID = new Set(Object.values(STAFF_ROLES));

export function getHomeroomStaffRoleValue(schoolType) {
    return getHomeroomTeacherTerm(schoolType).roleValue;
}

/** Role picker options — homeroom label follows primary vs secondary. */
export function getStaffRoleOptions(schoolType) {
    const term = getHomeroomTeacherTerm(schoolType);
    const homeroomOption = {
        value: term.roleValue,
        label: term.roleTitleCaps,
        description: `Homeroom lead: pick class and subjects, then create login. Enrolls pupils (director verifies) and submits term report cards.`,
    };
    return [BASE_ROLE_OPTIONS[0], homeroomOption, ...BASE_ROLE_OPTIONS.slice(1)];
}

/** @deprecated Use getStaffRoleOptions(schoolType) */
export const STAFF_ROLE_OPTIONS = getStaffRoleOptions();

export function normalizeStaffRole(role) {
    const r = String(role || "").trim();
    if (VALID.has(r)) return r;
    if (r.toLowerCase() === "bursar" || r.toLowerCase() === "bursor") return STAFF_ROLES.BURSAR;
    if (r.toLowerCase() === "librarian") return STAFF_ROLES.LIBRARIAN;
    if (/head\s*of\s*department/i.test(r) || r.toLowerCase() === "hod") return STAFF_ROLES.HOD;
    if (/class\s*teacher/i.test(r) || r.toLowerCase() === "class-teacher") return STAFF_ROLES.CLASS_TEACHER;
    if (/form\s*teacher/i.test(r) || r.toLowerCase() === "form-teacher") return STAFF_ROLES.FORM_TEACHER;
    return STAFF_ROLES.TEACHER;
}

export function isHomeroomTeacherRole(role) {
    const r = normalizeStaffRole(role);
    return r === STAFF_ROLES.FORM_TEACHER || r === STAFF_ROLES.CLASS_TEACHER;
}

/** @deprecated Use isHomeroomTeacherRole */
export function isFormTeacherRole(role) {
    return isHomeroomTeacherRole(role);
}

export function getStaffRoleLabel(role, schoolType) {
    const r = normalizeStaffRole(role);
    if (isHomeroomTeacherRole(r) && schoolType != null) {
        return getHomeroomTeacherTerm(schoolType).roleTitleCaps;
    }
    if (isHomeroomTeacherRole(r)) {
        return r === STAFF_ROLES.CLASS_TEACHER ? "Class Teacher" : "Form Teacher";
    }
    const opt = getStaffRoleOptions(schoolType).find((o) => o.value === r);
    return opt?.label || r;
}

/** Job title from logged-in staff user. Do not use portal `role` ("Teacher") as fallback. */
export function getStaffRoleFromUser(user) {
    if (!user) return STAFF_ROLES.TEACHER;
    if (user.staffRole != null && String(user.staffRole).trim() !== "") {
        return normalizeStaffRole(user.staffRole);
    }
    const r = String(user.role || "").trim();
    if (r && r !== "Teacher" && r !== "Admin" && r !== "Student") {
        return normalizeStaffRole(r);
    }
    return STAFF_ROLES.TEACHER;
}

export function isBursar(role) {
    return normalizeStaffRole(role) === STAFF_ROLES.BURSAR;
}

export function isBursarUser(user) {
    return isBursar(getStaffRoleFromUser(user));
}

export function isLibrarian(role) {
    return normalizeStaffRole(role) === STAFF_ROLES.LIBRARIAN;
}

export function isLibrarianUser(user) {
    return isLibrarian(getStaffRoleFromUser(user));
}

/** True when staff has at least one subject linked (director assigned under Staff). */
export function staffHasAssignedSubjects(user) {
    return getTeacherSubjects(user).length > 0;
}

/** Subject teachers, HODs, homeroom teachers — or a librarian with assigned subjects. */
export function showsTeachingPortal(user) {
    if (!user) return false;
    const role = getStaffRoleFromUser(user);
    if (isBursar(role)) return false;
    if (isTeachingStaff(role)) return true;
    if (isLibrarian(role) && staffHasAssignedSubjects(user)) return true;
    return false;
}

export function schoolHasLibrarianFromList(teachers) {
    if (!Array.isArray(teachers)) return false;
    return teachers.some((t) => isLibrarian(t?.staffRole ?? t?.role));
}

export function schoolHasVerifiedLibrarianFromList(teachers) {
    if (!Array.isArray(teachers)) return false;
    return teachers.some(
        (t) =>
            isLibrarian(t?.staffRole ?? t?.role) &&
            t?.profileCompleted === true &&
            t?.portalEnabled !== false,
    );
}

/** Director always has library analytics; inventory moves to the librarian portal when one is appointed. */
export function adminHasLibraryAnalyticsAccess() {
    return true;
}

export function adminLibraryManagedByLibrarian(
    currentUser,
    teachersList,
    { requireProfileCompleted = true } = {},
) {
    if (currentUser?.hasLibrarian === false) return false;
    if (Array.isArray(teachersList) && teachersList.length > 0) {
        return requireProfileCompleted
            ? schoolHasVerifiedLibrarianFromList(teachersList)
            : schoolHasLibrarianFromList(teachersList);
    }
    return currentUser?.hasLibrarian === true;
}

export function isTeachingStaff(role) {
    const r = normalizeStaffRole(role);
    return (
        r === STAFF_ROLES.TEACHER ||
        r === STAFF_ROLES.HOD ||
        r === STAFF_ROLES.FORM_TEACHER ||
        r === STAFF_ROLES.CLASS_TEACHER
    );
}

/** Subject pickers during staff setup — homeroom teachers often get subjects assigned later on Staff. */
export function staffRoleNeedsSubjectFlow(role) {
    const r = normalizeStaffRole(role);
    return r === STAFF_ROLES.TEACHER || r === STAFF_ROLES.HOD;
}

/** Class + subjects before credentials when adding a form / class teacher from Staff. */
export function staffRoleNeedsHomeroomSetupFlow(role) {
    return isHomeroomTeacherRole(role);
}

/** Active-subject switcher in the teacher sidebar (teaching staff + librarians with subjects). */
export function staffRoleShowsSubjectSelector(role, user = null) {
    if (user && isLibrarian(role)) {
        return staffHasAssignedSubjects(user);
    }
    return isTeachingStaff(role);
}

/** @deprecated Prefer staffRoleShowsSubjectSelector(role, user) for librarians. */
export function userShowsSubjectSelector(user) {
    if (!user) return false;
    return staffRoleShowsSubjectSelector(getStaffRoleFromUser(user), user);
}

export function schoolHasBursarFromList(teachers) {
    if (!Array.isArray(teachers)) return false;
    return teachers.some((t) => isBursar(t?.staffRole ?? t?.role));
}

/** True when bursar exists and has completed first-login profile setup. */
export function schoolHasVerifiedBursarFromList(teachers) {
    if (!Array.isArray(teachers)) return false;
    return teachers.some(
        (t) =>
            isBursar(t?.staffRole ?? t?.role) &&
            t?.profileCompleted === true &&
            t?.portalEnabled !== false,
    );
}

/** Directors always see fees & expenses (same live data as the bursar portal). */
export function adminHasFinanceAccess() {
    return true;
}

/** True when a bursar staff member handles day-to-day finance for this school. */
export function adminFinanceManagedByBursar(
    currentUser,
    teachersList,
    { requireProfileCompleted = true } = {},
) {
    if (currentUser?.hasBursar === false) return false;
    if (Array.isArray(teachersList) && teachersList.length > 0) {
        return requireProfileCompleted
            ? schoolHasVerifiedBursarFromList(teachersList)
            : schoolHasBursarFromList(teachersList);
    }
    return currentUser?.hasBursar === true;
}

export { isPrimarySchoolType };
